import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2, 
  BookOpen, 
  Users, 
  FileText, 
  DollarSign, 
  BarChart3,
  LogOut,
  ChevronRight,
  UserCheck,
  MapPin
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

const LOGO_URL = "/admixio-logo.png";

const DashboardLayout = ({ children, navItems }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const defaultNavItems = {
    super_admin: [
      { icon: <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />, label: 'Overview', path: '/admin' },
      { icon: <UserCheck className="w-5 h-5" strokeWidth={1.5} />, label: 'Pending Approvals', path: '/admin/approvals' },
      { icon: <MapPin className="w-5 h-5" strokeWidth={1.5} />, label: 'Canada Colleges', path: '/admin/canada-colleges' },
      { icon: <Building2 className="w-5 h-5" strokeWidth={1.5} />, label: 'Universities', path: '/admin/universities' },
      { icon: <BookOpen className="w-5 h-5" strokeWidth={1.5} />, label: 'Courses', path: '/admin/courses' },
      { icon: <Users className="w-5 h-5" strokeWidth={1.5} />, label: 'Users', path: '/admin/users' },
      { icon: <FileText className="w-5 h-5" strokeWidth={1.5} />, label: 'Applications', path: '/admin/applications' },
      { icon: <DollarSign className="w-5 h-5" strokeWidth={1.5} />, label: 'Commissions', path: '/admin/commissions' },
      { icon: <BarChart3 className="w-5 h-5" strokeWidth={1.5} />, label: 'Analytics', path: '/admin/analytics' },
    ],
    agent: [
      { icon: <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />, label: 'Overview', path: '/agent' },
      { icon: <Users className="w-5 h-5" strokeWidth={1.5} />, label: 'Students', path: '/agent/students' },
      { icon: <FileText className="w-5 h-5" strokeWidth={1.5} />, label: 'Applications', path: '/agent/applications' },
      { icon: <BookOpen className="w-5 h-5" strokeWidth={1.5} />, label: 'Course Search', path: '/agent/courses' },
      { icon: <DollarSign className="w-5 h-5" strokeWidth={1.5} />, label: 'Commissions', path: '/agent/commissions' },
      { icon: <BarChart3 className="w-5 h-5" strokeWidth={1.5} />, label: 'Market Insights', path: '/agent/insights' },
    ],
    institution: [
      { icon: <LayoutDashboard className="w-5 h-5" strokeWidth={1.5} />, label: 'Overview', path: '/institution' },
      { icon: <FileText className="w-5 h-5" strokeWidth={1.5} />, label: 'Applications', path: '/institution/applications' },
      { icon: <BookOpen className="w-5 h-5" strokeWidth={1.5} />, label: 'Courses', path: '/institution/courses' },
      { icon: <BarChart3 className="w-5 h-5" strokeWidth={1.5} />, label: 'Analytics', path: '/institution/analytics' },
    ]
  };

  const items = navItems || defaultNavItems[user?.role] || [];

  return (
    <div className="min-h-screen bg-[#0D0B1E] flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-[#2D2654] bg-[#0D0B1E] flex flex-col fixed h-screen" data-testid="sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-[#2D2654]">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={LOGO_URL}
              alt="Admixio" 
              className="h-10"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {items.map((item, index) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/admin' && item.path !== '/agent' && item.path !== '/institution' && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300",
                  isActive 
                    ? "bg-gradient-to-r from-[#00D4FF]/20 to-[#8B5CF6]/20 text-white border border-[#00D4FF]/30" 
                    : "text-[#B8B4CE] hover:bg-[#141029] hover:text-white"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <span className={isActive ? 'text-[#00D4FF]' : ''}>
                  {item.icon}
                </span>
                <span className="text-sm font-medium">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto text-[#00D4FF]" />}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-[#2D2654]">
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-[#6B678A] truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[#B8B4CE] hover:bg-[#EF4444]/10 hover:text-[#EF4444] transition-all"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-sm font-medium">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64">
        {/* Header */}
        <header className="h-16 border-b border-[#2D2654] bg-[#0D0B1E]/80 backdrop-blur-md sticky top-0 z-40 flex items-center px-6">
          <div className="flex items-center gap-2 text-sm text-[#6B678A]">
            <span className="capitalize">{user?.role?.replace('_', ' ')}</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{items.find(i => location.pathname === i.path || location.pathname.startsWith(i.path))?.label || 'Dashboard'}</span>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
